import * as grpcWeb from 'grpc-web';
import { convertErrorMessage, getAuthMetadata } from '../helpers/rpc';
import {
    Product as ProductPb,
    GetProductRequest,
    GetProductResponse,
    ProductStatus,
    Restriction,
    Shipping,
    SaveProductRequest,
    DeleteProductRequest,
    GenerateProductIdRequest,
    GenerateProductIdResponse,
    ChannelType
} from '../rpc/admin/products_pb';
import { AdminServiceClient } from '../rpc/admin/AdminServiceClientPb';
import { getBaseApiUrl } from '../helpers/instance';
import { logOut } from '../helpers/login';
import { withRouter, RouteComponentProps, Prompt } from 'react-router-dom';
import React, { Component, RefObject } from 'react';
import Helmet from 'react-helmet';
import Header from '../components/header';
import Notification from '../components/notification';
import Footer from '../components/footer';
import { ProductContext, ContextProps } from '../components/product/context';
import Input from '../components/forms/input';
import TextArea from '../components/forms/textarea';
import Option from '../components/forms/option';
import Select from '../components/forms/select';
import { Manufacturer } from '../rpc/admin/manufacturers_pb';
import { Category, CategoryDescriptor } from '../rpc/admin/categories_pb';
import Variant, { VariantValidation } from '../components/product/variant';
import { Supplier } from '../rpc/admin/suppliers_pb';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend'
import NotFound from '../components/errors/not_found';
import feather from '../img/feather-sprite.svg';

interface MatchParams {
    id: string;
}

interface ProductValidation {
    nameError?: string;
    slugError?: string;
    shortDescriptionError?: string;
    longDescriptionError?: string;
    variants?: Array<VariantValidation>;
}

interface CatalogProps { }

interface CatalogState {
    product?: ProductPb;
    original?: ProductPb;
    open: Array<boolean>;
    context?: ContextProps;
    categoryOptions?: Array<Option<Category>>;
    manufacturerOptions?: Array<Option<Manufacturer>>;
    isNew: boolean;
    editing: boolean;
    saving: boolean;
    deleting: boolean;
    deleteOpen: boolean;
    notFound: boolean;
    errorMessage: string;
    validation: ProductValidation;
}

class CatalogProduct extends Component<CatalogProps & RouteComponentProps<MatchParams>, CatalogState> {

    private headerTop: number;
    private headerRef: RefObject<HTMLDivElement>;
    private spacerRef: RefObject<HTMLDivElement>;

    constructor(props: CatalogProps & RouteComponentProps<MatchParams>) {
        super(props);
        this.headerTop = -1;
        this.headerRef = React.createRef<HTMLDivElement>();
        this.spacerRef = React.createRef<HTMLDivElement>();
        this.state = {
            open: [],
            isNew: false,
            editing: true,
            saving: false,
            deleting: false,
            deleteOpen: false,
            notFound: false,
            errorMessage: '',
            validation: {},
        };
    }

    private stackCategories(descs: Array<CategoryDescriptor>): Array<CategoryDescriptor> {
        const catMap = new Map<string, CategoryDescriptor>();
        const names: Array<string> = [];
        descs.forEach((desc) => {
            const chain: Array<string> = [];
            for (let target = desc.getCategory(); target !== Category.CATEGORY_UNKNOWN;) {
                for (let i = 0; i < descs.length; i++) {
                    if (descs[i].getCategory() === target) {
                        target = descs[i].getParent();
                        chain.push(descs[i].getName());
                        break;
                    }
                }
            }
            chain.reverse();
            const joined = chain.join(' > ');
            names.push(joined);
            catMap.set(joined, desc);
        });

        names.sort();

        const result: Array<CategoryDescriptor> = [];
        names.forEach((name) => {
            catMap.forEach((v, k) => {
                if (k === name) {
                    const desc = v.clone();
                    desc.setName(k);
                    result.push(desc);
                }
            });
        });

        return result;
    }

    componentDidMount() {
        let idString: string;
        const id = this.props.match.params.id;
        if (typeof id === 'string') {
            idString = id;
        } else {
            this.setState({
                notFound: true
            });
            return;
        }

        let itemId = 0;
        if (idString !== 'new') {
            itemId = parseInt(idString);
            if (itemId <= 0 || isNaN(itemId)) {
                this.setState({
                    notFound: true
                });
                return;
            }
        }

        this.setState({
            isNew: idString === 'new',
            errorMessage: '',
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new GetProductRequest();
        request.setId(itemId);
        client.getProduct(request, getAuthMetadata(), (err: grpcWeb.Error, response: GetProductResponse) => {
            if (!err) {
                const categoryOptions: Array<Option<Category>> = [];
                this.stackCategories(response.getCategoryDescriptorsList()).forEach((desc) => {
                    categoryOptions.push({
                        value: desc.getCategory(),
                        label: desc.getName(),
                    });
                });

                const manufacturerOptions: Array<Option<Manufacturer>> = [];
                response.getManufacturerDescriptorsList().forEach((desc) => {
                    manufacturerOptions.push({
                        value: desc.getManufacturer(),
                        label: desc.getName(),
                    });
                });

                const statusOptions: Array<Option<ProductStatus>> = [];
                response.getStatusDescriptorsList().forEach((desc) => {
                    statusOptions.push({
                        value: desc.getProductStatus(),
                        label: desc.getName(),
                    });
                });

                const supplierOptions: Array<Option<Supplier>> = [];
                response.getSupplierDescriptorsList().forEach((desc) => {
                    supplierOptions.push({
                        value: desc.getSupplier(),
                        label: desc.getName(),
                    });
                });

                const shippingOptions: Array<Option<Shipping>> = [];
                response.getShippingDescriptorsList().forEach((desc) => {
                    shippingOptions.push({
                        value: desc.getShipping(),
                        label: desc.getName(),
                    });
                });

                const restrictionOptions: Array<Option<Restriction>> = [];
                response.getRestrictionDescriptorsList().forEach((desc) => {
                    restrictionOptions.push({
                        value: desc.getRestriction(),
                        label: desc.getName(),
                    });
                });

                const product = response.getProduct()!;
                switch (product.getCategoriesList().length) {
                    case 0:
                        product.addCategories(response.getCategoryDescriptorsList()[0].getCategory());
                        break;
                    case 1:
                        break;
                    default:
                        product.setCategoriesList([product.getCategoriesList()[0]]);
                        break;
                }

                const open: Array<boolean> = [];
                const validation: ProductValidation = {
                    variants: []
                };
                for (let i = 0; i < product.getVariantsList().length; i++) {
                    open.push(true);
                    validation.variants?.push({});
                }

                let original = product.clone();
                if (idString === 'new') {
                    original = new ProductPb();
                }

                this.setState({
                    product: product,
                    original: original,
                    open: open,
                    validation: validation,
                    categoryOptions: categoryOptions,
                    manufacturerOptions: manufacturerOptions,
                    context: {
                        statusOptions: statusOptions,
                        supplierOptions: supplierOptions,
                        shippingOptions: shippingOptions,
                        restrictionOptions: restrictionOptions,
                        channelTypeDescriptors: response.getChannelTypeDescriptorsList(),
                        categoryDescriptors: response.getCategoryDescriptorsList(),
                        manufacturerDescriptors: response.getManufacturerDescriptorsList()
                    }
                });
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else if (err.code === grpcWeb.StatusCode.NOT_FOUND) {
                    this.setState({
                        notFound: true
                    });
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err)
                    });
                    console.error(err);
                }
            }
        });


        window.addEventListener('scroll', this.handleScroll);
    }

    componentWillUnmount = () => {
        window.removeEventListener('scroll ', this.handleScroll);
    }

    componentDidUpdate = () => {
        if (this.headerTop === -1 && this.headerRef.current) {
            this.headerTop = this.headerRef.current.getBoundingClientRect().top;
        }
    }

    private handleScroll = () => {
        const elem = this.headerRef.current!;
        const spacer = this.spacerRef.current!;
        if (this.headerTop >= 0 && elem && spacer) {
            if (window.pageYOffset > this.headerTop) {
                elem.classList.add('product-header-sticky');
                spacer.style.paddingTop = this.headerTop.toString() + 'px';
            } else {
                elem.classList.remove('product-header-sticky');
                spacer.style.paddingTop = '0';
            }
        }
    }

    private handleInputChange = (value: string, name?: string) => {
        const product = this.state.product;
        switch (name) {
            case 'slug':
                product?.setSlug(value);
                break;
            case 'name':
                product?.setName(value);
                break;
            case 'short-description':
                product?.setShortDescription(value);
                break;
            case 'long-description':
                product?.setLongDescription(value);
                break;
            default:
                return;
        }
        this.setState({ product: product });
    }

    private handleOnManufacturerChange = (m: Manufacturer) => {
        const product = this.state.product!;
        product.setManufacturer(m);
        this.setState({ product: product });
    }

    private handleOnCategoriesChange = (c: Category) => {
        const product = this.state.product!;
        product.setCategoriesList([c]);
        this.setState({ product: product });
    }

    private handleOnVariantChange = (index: number, variant: ProductPb.Variant) => {
        const product = this.state.product!;
        const variants = product.getVariantsList();
        variants[index] = variant;
        product.setVariantsList(variants);
        this.setState({ product: product });
    }

    private handlePromoteVariant = (index: number) => {
        const product = this.state.product!;
        const variants = product.getVariantsList();
        const v = variants[index];
        variants[index] = variants[index - 1];
        variants[index - 1] = v;
        product.setVariantsList(variants);
        this.setState({ product: product });
    }

    private handleDemoteVariant = (index: number) => {
        const product = this.state.product!;
        const variants = product.getVariantsList();
        const v = variants[index];
        variants[index] = variants[index + 1];
        variants[index + 1] = v;
        product.setVariantsList(variants);
        this.setState({ product: product });
    }

    private handleCopyVariant = (index: number) => {
        this.setState({
            editing: false,
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new GenerateProductIdRequest();
        request.setCount(1);
        client.generateProductId(request, getAuthMetadata(), (err: grpcWeb.Error, response: GenerateProductIdResponse) => {
            this.setState({
                editing: true
            });
            if (!err) {
                const product = this.state.product!;
                const variants = product.getVariantsList();
                const variant = variants[index].clone();
                variant.setStyle('Copy of ' + variant.getStyle());
                variant.setSku(response.getIdsList()[0])
                variants.push(variant);
                product.setVariantsList(variants);
                const open = this.state.open;
                open.push(open[index]);
                const validation = this.state.validation;
                validation.variants?.push({});
                this.setState({
                    product: product,
                    open: open,
                    validation: validation
                });
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });
    }

    private handleAddVariant = (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();

        this.setState({
            editing: false,
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new GenerateProductIdRequest();
        request.setCount(1);
        client.generateProductId(request, getAuthMetadata(), (err: grpcWeb.Error, response: GenerateProductIdResponse) => {
            this.setState({
                editing: true
            });
            if (!err) {
                const product = this.state.product!;
                const variants = product.getVariantsList();
                const variant = new ProductPb.Variant();
                variant.setSku(response.getIdsList()[0])
                const channel = new ProductPb.Variant.Channel();
                channel.setChannelType(ChannelType.HAWK_AMERICAN);
                variant.addChannels(channel);
                variants.push(variant);
                product.setVariantsList(variants);
                const open = this.state.open;
                open.push(true);
                const validation = this.state.validation;
                validation.variants?.push({});
                this.setState({
                    product: product,
                    open: open,
                    validation: validation
                });
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });
    }

    private handleDeleteVariant = (index: number) => {
        const product = this.state.product!;
        const variants = product.getVariantsList();
        product.setVariantsList(variants);
        variants.splice(index, 1);
        const open = this.state.open;
        open.splice(index, 1);
        const validation = this.state.validation;
        validation.variants?.splice(index, 1);
        this.setState({
            product: product,
            open: open,
            validation: validation
        });
    }

    private handleToggleVariantOpen = (index: number) => {
        const open = this.state.open;
        open[index] = !open[index];
        this.setState({ open: open });
    }

    private handleExpandAll = (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (this.state.product) {
            const open: Array<boolean> = [];
            for (let i = 0; i < this.state.product.getVariantsList().length; i++) {
                open.push(true);
            }
            this.setState({ open: open });
        }
    }

    private handleCollapseAll = (e: React.FormEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (this.state.product) {
            const open: Array<boolean> = [];
            for (let i = 0; i < this.state.product.getVariantsList().length; i++) {
                open.push(false);
            }
            this.setState({ open: open });
        }
    }

    private validate(): boolean {
        let valid = true;
        const product = this.state.product!;
        const validation = this.state.validation;
        const open = this.state.open;

        product.setName(product.getName().trim());
        validation.nameError = product.getName() ? '' : 'Name is required';
        valid = valid && !!!validation.nameError;

        product.setSlug(product.getSlug().trim().toLowerCase());
        validation.slugError = product.getSlug() ? '' : 'Slug is required';
        valid = valid && !!!validation.slugError;

        product.setShortDescription(product.getShortDescription().trim());
        validation.shortDescriptionError = product.getShortDescription() ? '' : 'Description is required';
        valid = valid && !!!validation.shortDescriptionError;

        product.setLongDescription(product.getLongDescription().trim());
        validation.longDescriptionError = product.getLongDescription() ? '' : 'Text is required';
        valid = valid && !!!validation.longDescriptionError;

        validation.variants = [];
        const variantSkus = new Set();
        product.getVariantsList().forEach((variant, index) => {
            const vv: VariantValidation = {};
            let variantValid = true;
            if (product.getVariantsList().length > 1) {
                variant.setStyle(variant.getStyle().trim());
                vv.styleError = variant.getStyle() ? '' : 'Required with multiple variants';
                valid = valid && !!!vv.styleError;
                variantValid = variantValid && !!!vv.styleError;

                variant.setSlug(variant.getSlug().trim().toLowerCase());
                vv.slugError = variant.getSlug() ? '' : 'Required with multiple variants';
                valid = valid && !!!vv.slugError;
                variantValid = variantValid && !!!vv.slugError;

                if (variant.getSlug()) {
                    if (variantSkus.has(variant.getSlug())) {
                        vv.slugError = 'Slugs must be unique between variants';
                        valid = false;
                        variantValid = false;
                    } else {
                        variantSkus.add(variant.getSlug());
                    }
                }
            }

            const channel = variant.getChannelsList()[0];
            if (channel.getPrice() <= 0) {
                vv.priceError = 'Price is required';
            } else if (variant.getMapp() > 0 && channel.getPrice() < variant.getMapp()) {
                vv.priceError = 'Price is below MAPP';
            }
            valid = valid && !!!vv.priceError;
            variantValid = variantValid && !!!vv.priceError;

            validation.variants?.push(vv);
            if (!variantValid) {
                open[index] = true;
            }
        });

        this.setState({
            product: product,
            errorMessage: valid ? '' : 'Dang! Correct the errors below.',
            open: open,
            validation: validation
        })

        return valid;
    }

    private handleSave = (e: React.FormEvent<HTMLButtonElement>) => {
        if (!this.validate()) {
            return;
        }

        this.setState({
            editing: false,
            saving: true
        });

        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new SaveProductRequest();
        request.setProduct(this.state.product);
        client.saveProduct(request, getAuthMetadata(), (err: grpcWeb.Error, response: ProductPb) => {
            this.setState({
                editing: true,
                saving: false
            });
            if (!err) {
                this.setState({
                    product: response,
                    original: response.clone()
                });
                if (this.state.isNew) {
                    this.props.history.replace('/catalog/' + response.getId());
                }
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else if (err.code === grpcWeb.StatusCode.ALREADY_EXISTS) {
                    const validation = this.state.validation;
                    validation.slugError = 'Slug is not unique';
                    this.setState({
                        errorMessage: 'Slugs must be unique',
                        validation: validation,
                    });
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });
    }

    private handleRevert = (e: React.FormEvent<HTMLButtonElement>) => {
        this.setState({ product: this.state.original?.clone() });
    }

    private handleKeyDown = (e: KeyboardEvent): any => {
        if (e.key === 'Escape') {
            this.handleDeleteCancel();
        } else if (e.key === 'Enter') {
            this.handleDeleteOk();
        }
    }

    private handleDelete = (e: React.FormEvent<HTMLButtonElement>) => {
        window.addEventListener('keydown', this.handleKeyDown);
        this.setState({
            editing: false,
            deleteOpen: true,
            deleting: false,
            errorMessage: '',
        });
    }

    private handleDeleteOk = () => {
        const client = new AdminServiceClient(getBaseApiUrl(), null, null);
        const request = new DeleteProductRequest();
        request.setId(this.state.product?.getId()!);
        client.deleteProduct(request, getAuthMetadata(), (err: grpcWeb.Error) => {
            window.removeEventListener('keydown', this.handleKeyDown);
            this.setState({
                editing: true,
                deleteOpen: false,
                deleting: false
            });
            if (!err) {
                if (this.props.history.length > 1) {
                    this.props.history.goBack();
                } else {
                    this.props.history.push('/catalog');
                }
            } else {
                if (err.code === grpcWeb.StatusCode.UNAUTHENTICATED || err.code === grpcWeb.StatusCode.PERMISSION_DENIED) {
                    logOut();
                } else {
                    this.setState({
                        errorMessage: convertErrorMessage(err),
                    });
                    console.error(err);
                }
            }
        });
    }

    private handleDeleteCancel = () => {
        window.removeEventListener('keydown', this.handleKeyDown);
        this.setState({
            editing: true,
            deleteOpen: false,
            deleting: false,
            errorMessage: '',
        });
    }

    render() {
        if (this.state.notFound) {
            return <NotFound />;
        }

        if (this.state.product && this.state.context) {
            const changed = this.state.product.toString() !== this.state.original?.toString();
            let variantNum = 0;
            return (<>
                <Helmet>
                    <title>{this.state.product.getName()} | Dashboard</title>
                </Helmet>

                <Header />

                <Prompt when={changed} message="Are you sure you want to leave? You have unsaved changes." />

                <DndProvider backend={HTML5Backend}>
                    <ProductContext.Provider value={this.state.context}>
                        <div className="container product">
                            <div className="columns product-header" ref={this.headerRef}>
                                <div className="column title-column">
                                    <h2 className="is-size-4 has-text-weight-bold">{this.state.product.getName()}</h2>
                                </div>

                                <div className="column has-text-right">
                                    <button className="button is-small"
                                        title="Expand All Variants"
                                        onClick={this.handleExpandAll}>
                                        <svg width="16" height="16" className="button-icon">
                                            <use href={feather + "#chevrons-down"} />
                                        </svg>
                                    </button>
                                    <button className="button is-small"
                                        title="Collapse All Variants"
                                        onClick={this.handleCollapseAll}>
                                        <svg width="16" height="16" className="button-icon">
                                            <use href={feather + "#chevrons-up"} />
                                        </svg>
                                    </button>
                                    <button className="button is-small"
                                        title="Add Variant"
                                        disabled={!this.state.editing}
                                        onClick={this.handleAddVariant}>
                                        Add Variant
                                    </button>
                                    <button className="button is-danger is-small"
                                        title="Delete"
                                        disabled={!this.state.editing}
                                        onClick={this.handleDelete}>
                                        Delete
                                    </button>
                                    <button className="button is-small"
                                        title="Revert"
                                        disabled={!changed || !this.state.editing}
                                        onClick={this.handleRevert}>
                                        Revert
                                    </button>
                                    <button className="button is-link is-small"
                                        title="Save" disabled={!changed || !this.state.editing}
                                        onClick={this.handleSave}>
                                        Save
                                    </button>
                                </div>
                            </div>

                            <div ref={this.spacerRef}></div>

                            <Notification className="product-notification" message={this.state.errorMessage} />

                            <div className="columns">
                                <div className="column"><Input label="Name"
                                    name="name"
                                    disabled={!this.state.editing}
                                    errorMessage={this.state.validation.nameError}
                                    value={this.state.product.getName()}
                                    onChange={this.handleInputChange} /></div>
                                <div className="column"><Input label="Slug"
                                    name="slug"
                                    disabled={!this.state.editing}
                                    errorMessage={this.state.validation.slugError}
                                    value={this.state.product.getSlug()}
                                    onChange={this.handleInputChange} /></div>
                                <div className="column"><Input label="SKU"
                                    disabled={true}
                                    value={this.state.product.getId().toString()} /></div>
                            </div>

                            <div className="columns">
                                <div className="column"><Input label="Description"
                                    name="short-description"
                                    disabled={!this.state.editing}
                                    errorMessage={this.state.validation.shortDescriptionError}
                                    value={this.state.product.getShortDescription()}
                                    onChange={this.handleInputChange} /></div>
                            </div>

                            <div className="columns">
                                <div className="column"><TextArea label="Text"
                                    name="long-description"
                                    disabled={!this.state.editing}
                                    errorMessage={this.state.validation.longDescriptionError}
                                    value={this.state.product.getLongDescription()}
                                    onChange={this.handleInputChange} /></div>
                            </div>

                            <div className="columns">
                                <div className="column"><Select<Manufacturer> label="Manufacturer"
                                    disabled={!this.state.editing}
                                    value={this.state.product.getManufacturer()}
                                    options={this.state.manufacturerOptions}
                                    onChange={this.handleOnManufacturerChange} /></div>
                                <div className="column"><Select<Category> label="Category"
                                    disabled={!this.state.editing}
                                    value={this.state.product.getCategoriesList()[0]}
                                    options={this.state.categoryOptions}
                                    onChange={this.handleOnCategoriesChange} /></div>
                            </div>

                            {
                                this.state.product.getVariantsList().map((variant) => {
                                    return (
                                        <Variant variant={variant}
                                            category={this.state.product!.getCategoriesList()[0]}
                                            open={this.state.open[variantNum]}
                                            validation={this.state.validation!.variants![variantNum]}
                                            editing={this.state.editing}
                                            key={variant.getSku()}
                                            index={variantNum++}
                                            count={this.state.product?.getVariantsList().length}
                                            onChange={this.handleOnVariantChange}
                                            onCopy={this.handleCopyVariant}
                                            onDelete={this.handleDeleteVariant}
                                            onToggleOpen={this.handleToggleVariantOpen}
                                            onPromote={this.handlePromoteVariant}
                                            onDemote={this.handleDemoteVariant} />
                                    );
                                })
                            }

                        </div>
                    </ProductContext.Provider>
                </DndProvider>

                {
                    this.state.deleteOpen && (
                        <div className="modal is-active">
                            <div className="modal-background"></div>
                            <div className="product-delete-modal">
                                <Notification className="image-notification" message={this.state.errorMessage} />
                                <div className="product-delete-modal-controls">
                                    <h2 className="is-size-5">Are you sure you want to delete "{this.state.product.getName()}"?</h2>
                                    <p>This action cannot be undone.</p>
                                    <button className={"button is-danger" + (this.state.deleting ? " is-loading" : "")}
                                        onClick={this.handleDeleteOk}
                                        disabled={this.state.deleting}>DELETE</button>
                                    <button className="button" onClick={this.handleDeleteCancel} disabled={this.state.deleting}>Cancel</button>
                                </div>
                            </div>
                        </div>
                    )
                }

                <Footer />
            </>);
        }
        return (<>
            <Helmet>
                <title>Catalog | Dashboard</title>
            </Helmet>

            <Header />

            <div className="container">
                <Notification className="product-notification" message={this.state.errorMessage} />
            </div>

            <Footer />
        </>);
    }
}

export default withRouter(CatalogProduct);
